/* ──────────────────────────────────────────────────────────────
   VitalProvider.jsx              (full file – safe to overwrite)
   Listens for the single 'vitals-update' event fired by dvm.js
   or by App.js when re-loading a cached scan.
   Each event is enriched with *_score fields using
   DocsunVitalsHelper before it reaches React components.
   ────────────────────────────────────────────────────────────── */
import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
} from 'react';

import DocsunVitalsHelper from '../api/DocsunVitalsHelper';

/* ---------- default shape ---------- */
const initialState = {
  /* raw vitals -------------------------------------------------- */
  heartRate   : null,
  respRate    : null,
  spo2        : null,
  systolic    : null,
  diastolic   : null,
  bloodSugar  : null,
  cholesterol : null,

  /* helper meta ------------------------------------------------- */
  updatedAt   : null,
  progressText: null,
  progressPercentage: 0,
  healthStatus: '',
  healthValue : 1,

  /* rating scores we compute below ------------------------------ */
  hr_score   : null,
  br_score   : null,
  spo2_score : null,
  bp_score   : null,
  bs_score   : null,
  chol_score : null,
};

/* ---------- reducer (immutable merge) -------------------------- */
function vitalReducer(state, payload) {
  return { ...state, ...payload };
}

/* ---------- context + convenience hook ------------------------ */
const VitalContext = createContext(initialState);
export const useVitals = () => useContext(VitalContext);

/* ---------- provider ------------------------------------------ */
export function VitalProvider({ children }) {
  const [state, dispatch] = useReducer(vitalReducer, initialState);

  useEffect(() => {
    /* converts raw → scores, then pushes to reducer */
    function handler(e) {
      const d = e.detail || {};

      /* build the small dict DocsunVitalsHelper expects */
      const vitalDict = {
        hr   : d.heartRate,
        br   : d.respRate,
        spo2 : d.spo2,
        syst : d.systolic,
        diast: d.diastolic,
        bs   : d.bloodSugar,
        chol : d.cholesterol,
      };

      /* compute scores */
      const helper = new DocsunVitalsHelper(vitalDict);

      /* merge raw data with score output */
      dispatch({ ...d, ...helper.output });
    }

    window.addEventListener('vitals-update', handler);
    return () => window.removeEventListener('vitals-update', handler);
  }, []);

  return (
    <VitalContext.Provider value={state}>
      {children}
    </VitalContext.Provider>
  );
}
